import React from 'react';
import { AlertBanner } from '../ui';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';

interface RouteGuardProps {
  children: React.ReactNode;
  requireSiteAdmin?: boolean;
  requireTenantAccess?: boolean;
  requireTenantOwnerOrAdmin?: boolean;
}

export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requireSiteAdmin = false,
  requireTenantAccess = false,
  requireTenantOwnerOrAdmin = false
}) => {
  const { isSiteAdmin, hasTenantAccess, isTenantOwner, isTenantAdmin, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-10">
        <LoadingSpinner className="w-12 h-12" />
      </div>
    );
  }

  // Check site admin requirement
  if (requireSiteAdmin && !isSiteAdmin) {
    return (
      <div className="p-4">
        <AlertBanner type="error" heading="Access Denied">
          You do not have permission to access this page. Site administrator privileges are required.
        </AlertBanner>
      </div>
    );
  }

  // Check tenant access requirement
  if (requireTenantAccess && !hasTenantAccess) {
    return (
      <div className="p-4">
        <AlertBanner type="error" heading="Access Denied">
          You do not have permission to access this page. You must be a member of an organization.
        </AlertBanner>
      </div>
    );
  }

  // Check tenant owner/admin requirement
  if (requireTenantOwnerOrAdmin && !(isTenantOwner || isTenantAdmin)) {
    return (
      <div className="p-4">
        <AlertBanner type="error" heading="Access Denied">
          You do not have permission to access this page. Organization owner or administrator privileges are required.
        </AlertBanner>
      </div>
    );
  }

  return <>{children}</>;
};
